<template>
  <lb-dialog
    :value="value"
    @input="$emit('input', $event)"
    heading="Upload File"
    width="600"
    :loading="loading"
    data-testid="step-input-data__upload-dialog"
  >
    <template v-slot:body>
      <v-row class="mt-0">

        <!-- File Type dropdown (Two/Three file mode) -->
        <v-col
          v-if="fileuploadeCount === TM_RECONCILIATION.FILE_TYPES.TWO_FILE || fileuploadeCount === TM_RECONCILIATION.FILE_TYPES.THREE_FILE"
          cols="12" class="py-2"
        >
          <lb-dropdown
            label="Data Source Type*"
            placeholder="Select data source type"
            :items="files"
            :value="twofileId"
            @input="$emit('update:twofileId', $event); $emit('file-type-change')"
            itemtext="name"
            itemvalue="_id"
            data-testid="step-input-data__filetype-dropdown"
          />
        </v-col>

        <!-- Upload Type dropdown -->
        <v-col cols="12" class="py-2">
          <lb-dropdown
            label="Upload Method*"
            placeholder="Select type of upload file"
            :items="uploadTypeItems"
            :value="fileuptype"
            @input="$emit('update:fileuptype', $event)"
            data-testid="step-input-data__uploadtype-dropdown"
          />
        </v-col>

        <!-- File Picker -->
        <v-col cols="12" class="py-2">
          <p class="caption mb-1 font-weight-bold">Files</p>
          <lb-file
            label="Browse Files"
            :drag="false"
            hidedetails
            :displayname="false"
            :multiple="true"
            accept=".xlsx,.xls,.xlsm,.xlsb,.csv"
            :value="fileuptypefile"
            @input="$emit('file-input-change', $event)"
            data-testid="step-input-data__file-input"
          />
          <p
            class="caption mt-2 mb-0 warning--text upload-order-note"
            data-testid="step-input-data__upload-order-note"
          >
            <v-icon color="warning" small class="mr-1">mdi-alert</v-icon>
            <span class="font-weight-bold">Note:</span>
            Files will be uploaded in the order listed below. You can reorder them using the up/down arrows before uploading.
          </p>
        </v-col>

        <!-- Selected Files List -->
        <v-col cols="12" class="py-2" v-if="fileuploadeList.length > 0">
          <lb-datatableaction
            :headers="fileTableHeaders"
            :tabledata="fileuploadeList"
            :bulkActions="false"
            :showToolbar="false"
            data-testid="step-input-data__selected-files-table"
          >
            <template v-slot:tbody>
              <tr
                v-for="(file, idx) in fileuploadeList"
                :key="file._uid"
                :class="{ 'low-opacity': file._valid === false }"
                data-testid="step-input-data__selected-file-row"
              >
                <td class="px-3 body-2" style="word-break: break-all;">{{ file.name }}</td>

                <td class="px-3 text-center">
                  <span
                    v-if="file._validating"
                    class="step-input-data__file-validating-icon"
                    data-testid="step-input-data__file-validating"
                  >
                    <v-progress-circular
                      indeterminate
                      :size="16"
                      :width="2"
                      color="primary"
                    />
                  </span>
                  <span
                    v-else-if="file._valid"
                    class="step-input-data__file-valid-icon"
                    data-testid="step-input-data__file-valid"
                  >
                    <v-icon small color="success">mdi-check</v-icon>
                  </span>
                  <span
                    v-else
                    class="step-input-data__file-invalid-icon"
                    data-testid="step-input-data__file-invalid"
                  >
                    <v-tooltip top content-class="tooltip-top" max-width="260">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small color="error" v-bind="attrs" v-on="on">mdi-close</v-icon>
                      </template>
                      <span>{{ file._error || 'Headers do not match required columns' }}</span>
                    </v-tooltip>
                  </span>
                </td>

                <td class="px-3 text-center">
                  <div class="d-inline-flex align-center">
                    <v-btn
                      icon
                      small
                      :disabled="idx === 0"
                      aria-label="Move file up"
                      @click.stop="$emit('move-file-up', idx)"
                      data-testid="step-input-data__selected-file-move-up-btn"
                    >
                      <v-icon small>mdi-arrow-up</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      :disabled="idx === fileuploadeList.length - 1"
                      aria-label="Move file down"
                      @click.stop="$emit('move-file-down', idx)"
                      data-testid="step-input-data__selected-file-move-down-btn"
                    >
                      <v-icon small>mdi-arrow-down</v-icon>
                    </v-btn>
                    <v-btn icon small color="error"
                      @click.stop="$emit('delete-file', idx)"
                      data-testid="step-input-data__selected-file-delete-btn">
                      <img src="/img/icons/tm-icons/Trash.svg" alt="Delete" data-testid="step-input-data__selected-file-delete-icon"/>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </template>
          </lb-datatableaction>
        </v-col>

        <v-col cols="12" class="py-2" v-else>
          <div class="text-center caption grey--text pa-6 rounded dashed_border">
            No files attached yet.
          </div>
        </v-col>

        <v-col cols="12" class="py-0" v-if="errorMessage">
          <v-alert dense text type="error" class="mb-0" data-testid="step-input-data__upload-error">
            {{ errorMessage }}
          </v-alert>
        </v-col>

      </v-row>
    </template>

    <template v-slot:actions>
      <span class="caption text--secondary">* Required</span>
      <v-spacer />
      <base-button
        label="Cancel"
        outlined
        class="mr-2"
        @click="$emit('cancel')"
        data-testid="step-input-data__upload-cancel-btn"
      />
      <v-tooltip top :disabled="!proceedDisabled" content-class="tooltip-top" max-width="280">
        <template v-slot:activator="{ on, attrs }">
          <span v-bind="attrs" v-on="on">
            <base-button
              label="Proceed"
              primary
              :loading="loading"
              :disabled="proceedDisabled"
              @click="$emit('confirm')"
              data-testid="step-input-data__upload-confirm-btn"
            />
          </span>
        </template>
        <span>{{ proceedDisabledTooltip }}</span>
      </v-tooltip>
    </template>
  </lb-dialog>
</template>

<script>
import { TM_RECONCILIATION } from '@/modules/financialclose/utils/constants.js';

export default {
  name: 'UploadDataDialog',

  props: {
    value:           { type: Boolean, default: false },
    loading:         { type: Boolean, default: false },
    fileuploadeCount:{ type: String,  default: '' },
    files:           { type: Array,   default: () => [] },
    twofileId:       { type: String,  default: '' },
    uploadTypeItems: { type: Array,   default: () => [] },
    fileuptype:      { type: String,  default: '' },
    fileuptypefile:  { type: [Array, Object, File], default: null },
    fileuploadeList: { type: Array,   default: () => [] },
    fileTableHeaders:{ type: Array,   default: () => [] },
    errorMessage:         { type: String,  default: '' },
    proceedDisabled:      { type: Boolean, default: false },
    proceedDisabledTooltip: { type: String, default: '' },
  },

  data() {
    return {
      TM_RECONCILIATION,
    };
  },

  mounted() {
    this._autoSelectFirstUploadType();
  },

  watch: {
    // Re-auto-select when items change (e.g. after file-type switch changes available options)
    uploadTypeItems(items) {
      if (items && items.length > 0) {
        this._autoSelectFirstUploadType();
      }
    },
    // Re-auto-select when fileuptype is cleared externally (e.g. on source type change)
    // uploadTypeItems watcher won't fire if the items list reference hasn't changed
    fileuptype(val) {
      if (!val) {
        this._autoSelectFirstUploadType();
      }
    },
  },

  methods: {
    _autoSelectFirstUploadType() {
      if (!this.uploadTypeItems || this.uploadTypeItems.length === 0) return;

      const first = this.uploadTypeItems[0];
      const firstVal = typeof first === 'object' ? first.value : first;

      // Check if the current value is still valid in the new items list
      const currentIsValid = this.fileuptype && this.uploadTypeItems.some(
        item => (typeof item === 'object' ? item.value : item) === this.fileuptype
      );

      // Only emit if no value is set, or the current value is no longer in the list
      if (!currentIsValid && this.fileuptype !== firstVal) {
        this.$emit('update:fileuptype', firstVal);
      }
    }
  },
};
</script>
<style lang="scss" scoped>
.dashed_border {
  border: 1px dashed var(--color-border-default, #e0e0e0);
}
.upload-order-note {
  .v-icon {
    font-size: 16px !important;
    height: 16px !important;
    width: 16px !important;
    vertical-align: text-top;
  }
}
</style>
